// SCR-STAFF-416 (AP-BIL-05؛ US-BIL-023، AC-BIL-023.2): صف صورتحساب‌های الکترونیک ناموفق — علت رد سامانه، متن سامانه،
// شمار تلاش و ارسال دوباره. ارسال ناهمگام است (Job دوره‌ای هر پنج دقیقه)، پس این صفحه فقط ردیف‌های نیازمند اقدام
// حسابدار و ردیف‌های پذیرفته‌شده را برای پیگیری نشان می‌دهد. تلاش دوباره با همان کلید Idempotency است (BR-INT-012).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type BilEInvoiceStatus, type BilEInvoiceSubmissionView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  faDigits,
  ListPage,
  StatusChip,
  toJalali,
  toJalaliDateTime,
  useDisplaySettings,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Amount, SCR_STAFF_416 } from './common';

type Filter = 'attention' | BilEInvoiceStatus;

const TONE: Record<BilEInvoiceStatus, StatusTone> = {
  Pending: 'pending',
  Accepted: 'done',
  Rejected: 'error',
  Failed: 'error',
  Cancelled: 'cancelled',
};

export function EInvoiceQueuePage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [filter, setFilter] = useState<Filter>('attention');
  const canRetry = access.can('bil.settings.manage');
  const key = ['billing', session?.tenantId, 'e-invoices', filter] as const;

  const queue = useQuery({
    queryKey: key,
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/billing/e-invoices', {
          params: { query: filter === 'attention' ? {} : { status: filter } },
        }),
      ),
  });

  const retry = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        tenantApi.POST('/api/v1/billing/e-invoices/{id}:retry', {
          params: { path: { id } },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => {
      idempotency.settle(error);
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const columns: DataTableColumn<BilEInvoiceSubmissionView>[] = [
    {
      id: 'status',
      headerKey: 'bil:e_invoice.status',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <StatusChip tone={TONE[row.status] ?? 'neutral'} labelKey={`bil:e_invoice_status.${row.status}`} />
          {row.isSample ? <Chip size="small" color="warning" label={t('bil:e_invoice.sample')} data-testid={`bil-einvoice-sample-${row.id}`} /> : null}
        </Stack>
      ),
    },
    {
      id: 'invoice',
      headerKey: 'bil:e_invoice.invoice',
      cell: (row) => (
        <Stack spacing={0.25}>
          <bdi dir="ltr" data-testid={`bil-einvoice-number-${row.id}`}>{row.invoiceNumber}</bdi>
          <Typography variant="caption" color="text.secondary">
            {t(`bil:e_invoice_kind.${row.kind}`)}
          </Typography>
        </Stack>
      ),
    },
    { id: 'total', headerKey: 'bil:e_invoice.total', cell: (row) => <Amount money={row.total} /> },
    { id: 'tax', headerKey: 'bil:e_invoice.tax', cell: (row) => <Amount money={row.tax} /> },
    { id: 'date', headerKey: 'bil:e_invoice.business_date', cell: (row) => toJalali(row.businessDate) },
    {
      id: 'reason',
      headerKey: 'bil:e_invoice.reason',
      cell: (row) => (
        <Stack spacing={0.25} data-testid={`bil-einvoice-reason-${row.id}`}>
          <span>
            {row.taxReference
              ? t('bil:e_invoice.tax_reference', { value: row.taxReference })
              : row.errorCode
                ? `${row.errorCode} · ${t(`bil:e_invoice_reason.${row.reason}`, { defaultValue: row.reason ?? '' })}`
                : '—'}
          </span>
          {row.providerMessage ? (
            <Typography variant="caption" color="text.secondary" data-testid={`bil-einvoice-provider-${row.id}`}>
              {row.providerMessage}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      id: 'attempts',
      headerKey: 'bil:e_invoice.attempts',
      cell: (row) => <span data-testid={`bil-einvoice-attempts-${row.id}`}>{faDigits(row.attempts)}</span>,
    },
    {
      id: 'created',
      headerKey: 'bil:e_invoice.created_at',
      cell: (row) => toJalaliDateTime(row.createdAt, { timeZone }),
    },
    {
      id: 'actions',
      headerKey: 'bil:common.actions',
      cell: (row) =>
        canRetry && (row.status === 'Rejected' || row.status === 'Failed') ? (
          <Button size="small" variant="outlined" onClick={() => retry.mutate(row.id)} disabled={retry.isPending} data-testid={`bil-einvoice-retry-${row.id}`}>
            {t('bil:e_invoice.retry')}
          </Button>
        ) : null,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_416}
      titleKey="bil:e_invoice.title"
      subtitleKey="bil:e_invoice.subtitle"
      filters={
        <TextField
          select
          size="small"
          label={t('bil:e_invoice.status')}
          value={filter}
          onChange={(event) => setFilter(event.target.value as Filter)}
          data-testid="bil-einvoice-filter"
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="attention">{t('bil:e_invoice.filter_attention')}</MenuItem>
          <MenuItem value="Pending">{t('bil:e_invoice_status.Pending')}</MenuItem>
          <MenuItem value="Accepted">{t('bil:e_invoice_status.Accepted')}</MenuItem>
          <MenuItem value="Cancelled">{t('bil:e_invoice_status.Cancelled')}</MenuItem>
        </TextField>
      }
    >
      <Stack spacing={2}>
        {queue.data && !queue.data.enabled ? (
          <Alert severity="info" data-testid="bil-einvoice-disabled">
            {t('bil:e_invoice.disabled')}
          </Alert>
        ) : null}
        <DataTable
          rows={queue.data?.items}
          columns={columns}
          getRowId={(row) => row.id}
          isLoading={queue.isLoading}
          error={queue.error}
          onRetry={() => void queue.refetch()}
          emptyTitleKey="bil:e_invoice.empty"
          captionKey="bil:e_invoice.title"
        />
      </Stack>
    </ListPage>
  );
}
